import { useState, useEffect } from 'react';

/**
 * Subscribe to a CSS media query and re-render when it flips.
 *
 * Returns `false` during SSR / when matchMedia is unavailable so the desktop
 * markup is the default.
 */
export function useMediaQuery(query) {
  const getMatch = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false;

  const [matches, setMatches] = useState(getMatch);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mql = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);
    setMatches(mql.matches);
    // Safari < 14 only supports the deprecated addListener API.
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, [query]);

  return matches;
}

/**
 * Phone breakpoint shared with the `@media (max-width: 768px)` rules in
 * styles/app.css. Keep the two in sync.
 */
export const MOBILE_MEDIA_QUERY = '(max-width: 768px)';

export function useIsMobile() {
  return useMediaQuery(MOBILE_MEDIA_QUERY);
}
